import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useOutletContext } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { useLocaleNavigate } from "@ct-react/locale";
import { useCartBadge } from "../../hooks/cart-step-check";
import { useLazyLocalFetcher } from "../../hooks/local-fetcher";
import { User } from "@shared/models/user";
import {CartCustomer, CartItem as CartItemModel} from "../../models/cart";
import { CartContext } from "./cart-outlet";
import PaymentInformationForm from "../../components/forms/payment-information";
import "./common.scss";
import {gql, useQuery} from "@apollo/client";
import {FULL_CART_GQL_DATA, SHORT_ARTICLE_GQL_METADATA} from "@shared/gql/queries";
import {AnyPrice, castPrice, toGQLDate} from "@ct-react/core";
import {BookFullPrice} from "@ct-react/calendar";


const CartInformation = () => {

  const intl = useIntl();
  const navigate = useLocaleNavigate();
  const { state } = useLocation();
  const itemsCount = useCartBadge();
  const { userId, sessionId, changeIndex } = useOutletContext<CartContext>();

  const [ loading, setLoading ] = useState<boolean>(true);
  const [ editVisitorInfo, setEditVisitorInfo ] = useState<CartCustomer>(state?.visitorInfo);
  const [ visitorInfo, setVisitorInfo ] = useState<CartCustomer | undefined>(undefined);
  const { data } = useQuery(FULL_CART_GQL_DATA, {
    variables: { sessionId, userId },
    onCompleted: () => changeIndex(1)
  });
  const firstCartItem: CartItemModel | undefined =
    data?.cart?.items?.length ? (data.cart.items[0] as CartItemModel) : undefined;
  const articleId = firstCartItem?.articleId;
  const period = firstCartItem?.period;

  useQuery(SHORT_ARTICLE_GQL_METADATA, {
    variables: { articleId },
    skip: !articleId,
    ssr: false,
    onCompleted: ({ detail, agency }) => {
      setContent({ summary: detail, agency });
    }
  });
  const [ getUserProfile ] = useLazyLocalFetcher<User>("user");
  const [content, setContent] = useState<any | null>(null);


  useEffect(() => {
    if (!userId || !!state?.visitorInfo) return;
    const fetchUser = async () => await getUserProfile();
    fetchUser().then(profile => {
      const { address, ...others } = profile;
      setEditVisitorInfo({ ...others, address } as CartCustomer);
    });
  }, [ userId ]);

  useEffect(() => {
    itemsCount === 0 && navigate("/cart", { state });
    (!userId && !state?.chosenVisitorType) && navigate("/cart/account");
    setLoading(false);
    changeIndex(2);
  }, []);

  const onFormChange = (value: CartCustomer, isValid: boolean) => {
    if (!isValid) return;
    setVisitorInfo(value);
  }

  // rendering

  if (loading)
    return (<Skeleton className="cart-skeleton" />);
  const isReady = !!data?.cart && !!period && !!content?.summary;

  let checkin: any, checkout: any;
  if (isReady) {
    [checkin, checkout] = period as any[]; // period is ['YYYY-MM-DD','YYYY-MM-DD']
  }
  return (
    <div className="cart-item-wrapper">

      <PaymentInformationForm
        className="rl-two-col"
        data={editVisitorInfo}
        onChange={onFormChange}
      />

      <div className="rl-two-col stepper-actions">
        {/* Back is ALWAYS rendered */}
        <button
          type="button"
          className="step-btn back"
          onClick={() => navigate("/cart", { state })}
        >
          {intl.formatMessage({
            id: "cart-process-back-cart-button",
            defaultMessage: "Modifier votre réservation",
          })}
        </button>

        {/* Next is rendered only when data is ready */}
        {isReady && (
          <button
            type="button"
            className="step-btn"
            onClick={() =>
              navigate("/cart/payment", {
                state: {
                  ...state,
                  ...(!!userId && { chosenVisitorType: true }),
                  visitorInfo,
                },
              })
            }
            disabled={!visitorInfo}
            {...{
              "data-tm-id": "to-payment-method",
              "data-name": content!.summary.technicalName,
              "data-agency": content!.agency.name,
              "data-provider": content!.agency.id,
              "data-price": castPrice(
                (data!.cart.fullPrice as BookFullPrice).amount as AnyPrice
              ).amount,
              "data-checkin": toGQLDate(checkin),
              "data-checkout": toGQLDate(checkout),
            }}
          >
            {intl.formatMessage({
              id: "cart-process-next-payment-button",
              defaultMessage: "Définir le modèle de paiement",
            })}
          </button>
        )}
      </div>
    </div>
  );


};

export default CartInformation;
